/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useState } from 'react';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { isNestedEntry, NestedEntry, SingleEntry } from '../../graphql-fragments/navigationMenu';
import { capitalize, sum } from '../../utils/nodash';
import { getUrlFromVersatileLink } from '../../utils/sanity';

import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, AUTHOR_NAME, DEFAULT_LANG } from '../../constants';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import lisbonVenuesDarkLogo from '../../images/lisbon_venues_logo_dark.svg';
import lisbonVenuesWhiteLogo from '../../images/lisbon_venues_logo_white.svg';
import { BackgroundColorTheme } from '../../templates/Page';
import { getLocalizedUrlPath } from '../../utils/i18n';
import { checkIsInternalUrl, clsx } from '../../utils/utils';
import ButtonLink from './ButtonLink';
import DropDownMenu from './DropDownMenu';
import * as styles from './Header.module.scss';
import Menu from './Menu';
import MenuButton from './MenuButton';
import MultilanguageSelector from './MultilanguageSelector';
import SmartLink from './SmartLink';

export interface HeaderProps {
  sticky?: boolean;
  darkLogoImage: SanityImageType;
  lightLogoImage: SanityImageType;
  entries?: Array<SingleEntry | NestedEntry>;
  ctaButtons?: Array<ButtonLinkType>;
  legalLinks: Array<ButtonLinkType>;
  theme?: BackgroundColorTheme;
}

const Header = ({
  sticky,
  darkLogoImage,
  lightLogoImage,
  entries,
  ctaButtons,
  legalLinks,
  theme,
}: HeaderProps): React.ReactElement => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // NOTE: this could probably be replaced by calculating the menu size
  // automatically with javascript.
  // TODO Check these sizes on each project
  const MENU_GAP_SIZE = 16; // spacing-medium
  const MENU_ENTRY_PADDING_SIZE = 0; // spacing-x-small
  const MENU_CHAR_SIZE = 9;
  const MENU_EXTERNAL_URL_ICON_SIZE = 20;
  // Small menus are shown from tablet vertical upwards
  const MEDIUM_MENU_MIN_SIZE = 500; // Fits only from table horizontal upwards
  const LARGE_MENU_MIN_SIZE = 700; // Fits only from laptop upwards
  const HUGE_MENU_MIN_SIZE = 800; // Doesn't fit anywhere

  // Calculates the menu size so we can inject corresponding classes
  // to hide the menu when it doesn't fit anymore
  const menuApproximateSize =
    entries &&
    entries.filter(
      entry => !isNestedEntry(entry) && !checkIsInternalUrl(getUrlFromVersatileLink(entry)),
    ).length *
      MENU_EXTERNAL_URL_ICON_SIZE +
      sum(entries.map(({ title }) => title.length * MENU_CHAR_SIZE + MENU_ENTRY_PADDING_SIZE * 2)) +
      (entries.length - 1) * MENU_GAP_SIZE;

  const menuSizeCategory =
    menuApproximateSize !== undefined &&
    (menuApproximateSize >= HUGE_MENU_MIN_SIZE
      ? 'huge'
      : menuApproximateSize >= LARGE_MENU_MIN_SIZE
        ? 'large'
        : menuApproximateSize >= MEDIUM_MENU_MIN_SIZE
          ? 'medium'
          : 'small');

  let pathWithoutCountry = typeof location !== 'undefined' ? location.pathname : '';
  if (
    ALL_LANGS.filter(lang => lang !== DEFAULT_LANG).filter(lang =>
      pathWithoutCountry.match(new RegExp(`^/${lang}($|/)`)),
    ).length > 0
  ) {
    pathWithoutCountry = pathWithoutCountry.substring(3) || '/';
  }

  const { i18n } = useTranslation();

  return (
    <>
      {sticky && (
        <Helmet>
          <html className={styles.html}></html>
        </Helmet>
      )}
      <header
        className={clsx(
          styles.header,
          menuSizeCategory
            ? styles['headerWithMenu' + capitalize(menuSizeCategory)]
            : styles.headerWithMenuSmall,
          sticky && styles.stickyHeader,
        )}
        data-theme={theme}
      >
        <div className={styles.headerContainer}>
          <SmartLink to="/" className={styles.logoWrapper}>
            <div className={styles.logoContainer}>
              <img
                src={`${
                  theme === 'moonlit' || theme === 'starlit'
                    ? lightLogoImage.asset.url
                    : darkLogoImage.asset.url
                }?w=300&auto=format&q=75`}
                alt=""
                fetchPriority="high"
                decoding="async"
              />
              <div className={styles.visuallyHidden}>{AUTHOR_NAME}</div>
            </div>
            <div className={styles.logoSeparator}></div>
            <div className={styles.partnerLogoContainer}>
              <img
                src={
                  theme === 'moonlit' || theme === 'starlit'
                    ? lisbonVenuesWhiteLogo
                    : lisbonVenuesDarkLogo
                }
                alt="Lisbon Venues"
              />
            </div>
          </SmartLink>

          {entries && <Menu entries={entries} className={styles.desktopMenu}></Menu>}

          <div className={styles.linksContainer}>
            {ctaButtons && ctaButtons.length > 0 && (
              <div className={styles.ctaButtonsContainer}>
                {ctaButtons.map((ctaButton, i) => (
                  <ButtonLink key={i} button={ctaButton}></ButtonLink>
                ))}
              </div>
            )}

            {ALL_LANGS.length > 1 && (
              <div className={styles.multilingueContainer}>
                <MultilanguageSelector
                  title={i18n.language}
                  languages={ALL_LANGS.map(lang => ({
                    title: lang,
                    linkType: 'externalLink',
                    externalLink: { url: getLocalizedUrlPath(pathWithoutCountry, lang) },
                  }))}
                />
              </div>
            )}

            <MenuButton
              className={styles.menuButton}
              isMenuOpen={isMenuOpen}
              onTouch={() => {
                setIsMenuOpen(!isMenuOpen);
              }}
            ></MenuButton>
          </div>

          <div className={styles.dropDownMenuWrapperWindow}>
            <div
              className={clsx(
                styles.dropDownMenuWrapper,
                isMenuOpen ? styles.menuOpen : styles.menuClosed,
              )}
            >
              <DropDownMenu
                entries={entries}
                legalLinks={legalLinks}
                ctaButtons={ctaButtons}
                onEntryClick={() => {
                  setIsMenuOpen(false);
                }}
                onCtaButtonClick={() => {
                  setIsMenuOpen(false);
                }}
                theme={theme}
              ></DropDownMenu>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
